import type { JSX } from 'react';
import React, { useEffect, useMemo, useRef } from 'react';

import type { DefineSlots } from '../utils';
import type { WithSlotProps } from '~/components/hocs';
import { withSlot } from '~/components/hocs';
import { componentRegistry, componentSlots, renderChild } from '~/components/layout/utils';
import { SlotsProvider } from '~/components/providers';
import { useTemplate } from '~/hooks';
import { useGetComments } from '~/hooks/api/use-get-comments';
import { selectIsOpen, useStore } from '~/store';
import { Comment } from '../comment';

export const NAME = 'Comments';

export const Slots = componentSlots(NAME, {
  COMMENTS: 'comments',
});

type SlotProps = DefineSlots<
  typeof Slots,
  {
    [Slots.COMMENTS]: JSX.IntrinsicElements['div'];
  }
>;

export interface Props extends React.PropsWithChildren, WithSlotProps {
  className?: string;
}

const CommentsComponent = (_props: Props) => {
  const isEmbedOpen = useStore(selectIsOpen);
  const { query, comments } = useGetComments({ enabled: isEmbedOpen });
  const { hasNextPage, isFetchingNextPage, isPending, fetchNextPage } = query;
  const template = useTemplate(NAME);
  const noCommentsPlaceholder = useTemplate('NoCommentsPlaceholder');
  const skeletonTemplate = useTemplate('CommentSkeleton');
  const endOfListRef = useRef<HTMLDivElement>(null);

  const slots = useMemo<SlotProps>(() => {
    const children: JSX.Element[] = [...comments.map(comment => <Comment commentId={comment.id} key={comment.id} />)];

    if (comments.length && hasNextPage) {
      children.push(<div ref={endOfListRef} key="endOfList" style={{ height: '10px' }} />);
    }
    if (isFetchingNextPage || isPending) {
      children.push(...[1, 2, 3, 4, 5].map((_, i) => renderChild(skeletonTemplate, i)));
    } else if (!comments.length) {
      children.push(renderChild(noCommentsPlaceholder));
    }

    // TODO: render error

    return {
      [Slots.COMMENTS]: {
        children: children.filter(Boolean),
      },
    };
  }, [comments, skeletonTemplate, isPending, isFetchingNextPage, hasNextPage, noCommentsPlaceholder]);

  useEffect(() => {
    const onWindowScroll = () => {
      const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
      console.log('scroll ratio:', window.scrollY / maxScroll, 'hasNextPage:', hasNextPage);
      if (window.scrollY / maxScroll > 0.95 && hasNextPage) {
        void fetchNextPage();
      }
    };
    window.addEventListener('scroll', onWindowScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', onWindowScroll);
    };
  }, [hasNextPage, fetchNextPage]);

  return <SlotsProvider slots={slots}> {renderChild(template)}</SlotsProvider>;
};

CommentsComponent.displayName = NAME;

export const Comments = withSlot(CommentsComponent);
componentRegistry.add(NAME, Comments as React.FC);
